import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Loader2,
    User,
    Building2,
    FlaskConical,
    IndianRupee,
    Download,
    FileText,
    Calendar,
    Stethoscope,
    Receipt,
    Pencil,
    Check,
    X
} from "lucide-react";
import { toast } from 'sonner';
import { billingApi } from "@/api/billing";
import { paymentApi } from "@/api/payment";
import { Bill, PaymentMethod, PaymentCreateData, BillStatus, PaymentStatus } from "@/types/types";
import { formatDate, formatDateTime } from "@/utils/dateUtils";
import { ensureDoctorPrefix } from "@/lib/doctorName";
import PaymentSplitsEditor, { getSplitsError, getSplitsTotal, PaymentSplitDraft } from "@/components/billing/PaymentSplitsEditor";
import LoyaltyBillingPanel from "@/components/billing/LoyaltyBillingPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useCanFinalizeOutpatientDraftBill } from "@/hooks/useFrontDeskBasePath";

const ADMIN_EDITABLE_LINE_ITEM_TYPES = new Set([
    'OPD_CONSULTATION',
    'PROCEDURE',
    'LAB_TEST',
]);

function canEditLineUnitPrice(params: {
    itemType: string;
    isOutpatientBillAdminEditor: boolean;
    userRole?: string;
    billPaidAmount: number;
    paymentCount: number;
}): boolean {
    const { itemType, isOutpatientBillAdminEditor, userRole, billPaidAmount, paymentCount } =
        params;
    if (ADMIN_EDITABLE_LINE_ITEM_TYPES.has(itemType) && isOutpatientBillAdminEditor) {
        return true;
    }
    if (
        itemType === 'OPD_CONSULTATION' &&
        userRole === 'RECEPTIONIST' &&
        billPaidAmount === 0 &&
        paymentCount === 0
    ) {
        return true;
    }
    return false;
}

interface ViewBillProps {
    billId: string | null;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export default function ViewBill({
    billId,
    isOpen,
    onClose,
    onSuccess
}: ViewBillProps) {
    const { user } = useAuth();
    const isOutpatientBillAdminEditor =
        user?.role === 'HOSPITAL_ADMIN' ||
        user?.role === 'SUPER_ADMIN' ||
        user?.role === 'BUSINESS_ANALYST';
    const canFinalizeDraftBill = useCanFinalizeOutpatientDraftBill();
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [paymentType, setPaymentType] = useState<'full' | 'partial'>('full');
    const [splits, setSplits] = useState<PaymentSplitDraft[]>([{ mode: PaymentMethod.CASH, amount: "" }]);
    const [paymentNotes, setPaymentNotes] = useState<string>("");
    const [processingPayment, setProcessingPayment] = useState(false);
    const [downloadingBill, setDownloadingBill] = useState(false);
    const queryClient = useQueryClient();
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [unitPriceDraft, setUnitPriceDraft] = useState<string>("");
    const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
    const [paymentMethodDraft, setPaymentMethodDraft] = useState<PaymentMethod>(PaymentMethod.CASH);

    const updateBillItemMutation = useMutation({
        mutationFn: async (payload: { billId: string; itemId: string; unitPrice: number }) =>
            billingApi.updateBillItem(payload.billId, payload.itemId, { unitPrice: payload.unitPrice }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bill', billId] });
            toast.success('Line amount updated');
            setEditingItemId(null);
            setUnitPriceDraft("");
            if (onSuccess) onSuccess();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to update amount');
        },
    });

    // Fetch bill data
    const { data: bill, isLoading, error } = useQuery<Bill>({
        queryKey: ['bill', billId],
        queryFn: async () => {
            if (!billId) throw new Error('Bill ID is required');
            const response = await billingApi.getBillById(billId);
            return response.data?.data;
        },
        enabled: !!billId && isOpen
    });

    // Process payment mutation
    const processPaymentMutation = useMutation({
        mutationFn: async (payments: PaymentCreateData[]) => paymentApi.bulkProcessPayments(payments),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bill', billId] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            toast.success('Payment processed successfully');
            setShowPaymentDialog(false);
            setProcessingPayment(false);
            resetPaymentForm();
            if (onSuccess) onSuccess();
        },
        onError: (error: any) => {
            console.error('Error processing payment:', error);
            toast.error(error.response?.data?.message || 'Failed to process payment');
            setProcessingPayment(false);
        },
    });

    const updatePaymentMethodMutation = useMutation({
        mutationFn: async ({ paymentId, paymentMethod }: { paymentId: string; paymentMethod: PaymentMethod }) =>
            paymentApi.updatePaymentMethod(paymentId, { paymentMethod }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bill', billId] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            toast.success('Payment mode updated');
            setEditingPaymentId(null);
            if (onSuccess) onSuccess();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to update payment mode');
        },
    });

    // Refund payment mutation
    const cancelDraftBillMutation = useMutation({
        mutationFn: async () => {
            if (!bill?.id) throw new Error('No bill');
            return billingApi.updateBillStatus(bill.id, { status: BillStatus.CANCELLED });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bill', billId] });
            toast.success('Bill cancelled');
            if (onSuccess) onSuccess();
            onClose();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to cancel bill');
        },
    });

    const markDraftPaidMutation = useMutation({
        mutationFn: async () => {
            if (!bill?.id) throw new Error('No bill');
            return billingApi.updateBillStatus(bill.id, { status: BillStatus.PAID });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bill', billId] });
            toast.success('Bill marked as paid');
            if (onSuccess) onSuccess();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Failed to update bill');
        },
    });

    const refundPaymentMutation = useMutation({
        mutationFn: async ({ paymentId, reason }: { paymentId: string; reason?: string }) => {
            return await paymentApi.refundPayment(paymentId, { reason });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bill', billId] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            toast.success('Refund processed successfully');
            if (onSuccess) onSuccess();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Refund failed');
        },
    });

    // Download bill PDF mutation
    const downloadBillMutation = useMutation({
        mutationFn: async (billId: string) => {
            const response = await billingApi.exportBillPDF(billId);
            return response;
        },
        onSuccess: (response) => {
            // Create blob from response data
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            // Create temporary link and trigger download (not open in viewer)
            const link = document.createElement('a');
            link.href = url;
            link.download = `Bill-${bill?.billNumber || 'unknown'}.pdf`;
            link.style.display = 'none'; // Hide the link
            link.target = '_self'; // Prevent opening in new tab
            document.body.appendChild(link);
            link.click();
            
            // Clean up after a short delay
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);

            toast.success('Bill PDF downloaded successfully');
            setDownloadingBill(false);
        },
        onError: (error: any) => {
            console.error('Error downloading bill PDF:', error);
            toast.error(error.response?.data?.message || 'Failed to download bill PDF');
            setDownloadingBill(false);
        },
    });

    const getStatusColor = (status: BillStatus) => {
        switch (status) {
            case 'PAID':
                return 'bg-green-100 text-green-800';
            case 'PARTIALLY_PAID':
                return 'bg-yellow-100 text-yellow-800';
            case 'OVERDUE':
                return 'bg-red-100 text-red-800';
            case 'DRAFT':
                return 'bg-gray-100 text-gray-800';
            case 'GENERATED':
            case 'SENT':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getPaymentStatusColor = (status: PaymentStatus) => {
        switch (status) {
            case 'COMPLETED':
                return 'bg-green-100 text-green-800';
            case 'PENDING':
                return 'bg-yellow-100 text-yellow-800';
            case 'FAILED':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const handlePaymentClick = (type: 'full' | 'partial') => {
        setPaymentType(type);
        const due = bill?.dueAmount ?? 0;
        setSplits([{ mode: PaymentMethod.CASH, amount: type === "full" ? String(due) : "" }]);
        setShowPaymentDialog(true);
    };

    const resetPaymentForm = () => {
        setPaymentType('full');
        setSplits([{ mode: PaymentMethod.CASH, amount: "" }]);
        setPaymentNotes("");
    };

    const handlePaymentSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!bill?.id) {
            toast.error('No bill found');
            return;
        }

        const splitsErr = getSplitsError({ splits, maxTotal: bill.dueAmount });
        if (splitsErr) {
            toast.error(splitsErr);
            return;
        }

        setProcessingPayment(true);

        try {
            const payments: PaymentCreateData[] = splits.map((sp) => ({
                billId: bill.id,
                amount: Number(sp.amount),
                paymentMethod: sp.mode,
                notes: paymentNotes || undefined,
            }));
            await processPaymentMutation.mutateAsync(payments);
        } catch (error) {
            console.error('Error processing payment:', error);
            toast.error('Failed to process payment');
            setProcessingPayment(false);
        }
    };

    const handleDownloadBill = async () => {
        if (!bill?.id) {
            toast.error('No bill found');
            return;
        }

        setDownloadingBill(true);
        try {
            await downloadBillMutation.mutateAsync(bill.id);
        } catch (error) {
            console.error('Error downloading bill:', error);
            setDownloadingBill(false);
        }
    };

    const handlePrint = async () => {
        if (!billId) return;

        // Open popup window immediately with loading message
        const printWindow = window.open('', '_blank', 'width=800,height=600,left=100,top=100');
        
        if (!printWindow) {
            toast.error('Please allow popups to print the bill');
            return;
        }

        // Show loading message in popup
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Loading Bill...</title>
                <style>
                    body {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        height: 100vh;
                        margin: 0;
                        font-family: Arial, sans-serif;
                        background: #f5f5f5;
                    }
                    .spinner {
                        border: 4px solid #f3f3f3;
                        border-top: 4px solid #3498db;
                        border-radius: 50%;
                        width: 40px;
                        height: 40px;
                        animation: spin 1s linear infinite;
                        margin-bottom: 20px;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    .text {
                        color: #666;
                        font-size: 16px;
                    }
                </style>
            </head>
            <body>
                <div class="spinner"></div>
                <div class="text">Loading bill for printing...</div>
            </body>
            </html>
        `);
        printWindow.document.close();

        try {
            // Fetch PDF from endpoint (letterhead mode: blank header/footer for printing on letterhead)
            const response = await billingApi.exportBillPDF(billId, { letterhead: true });
            
            // Create blob from PDF response
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            // Load PDF in the popup
            printWindow.location.href = url;

            // Wait for PDF to load, then trigger print immediately
            const triggerPrint = () => {
                try {
                    if (printWindow && !printWindow.closed) {
                        printWindow.focus();
                        printWindow.print();
                    }
                } catch (e) {
                    console.error('Print error:', e);
                }
            };

            // Multiple attempts to ensure PDF loads before printing
            setTimeout(triggerPrint, 1000);
            setTimeout(triggerPrint, 2500);
            setTimeout(triggerPrint, 4000);

            // Close window after printing
            printWindow.addEventListener('afterprint', () => {
                setTimeout(() => {
                    if (printWindow && !printWindow.closed) {
                        printWindow.close();
                    }
                    window.URL.revokeObjectURL(url);
                }, 100);
            });

            // Cleanup on window close
            printWindow.addEventListener('beforeunload', () => {
                window.URL.revokeObjectURL(url);
            });

            // Fallback cleanup after 60 seconds
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 60000);
        } catch (error: any) {
            console.error('Error getting print PDF:', error);
            
            // Show error in popup
            if (printWindow && !printWindow.closed) {
                printWindow.document.open();
                printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>Error</title>
                        <style>
                            body {
                                display: flex;
                                flex-direction: column;
                                justify-content: center;
                                align-items: center;
                                height: 100vh;
                                margin: 0;
                                font-family: Arial, sans-serif;
                                background: #f5f5f5;
                            }
                            .error {
                                color: #e74c3c;
                                text-align: center;
                                padding: 20px;
                            }
                            button {
                                margin-top: 20px;
                                padding: 10px 20px;
                                background: #3498db;
                                color: white;
                                border: none;
                                border-radius: 4px;
                                cursor: pointer;
                            }
                            button:hover {
                                background: #2980b9;
                            }
                        </style>
                    </head>
                    <body>
                        <div class="error">
                            <h3>Error loading bill</h3>
                            <p>${error.response?.data?.message || 'Failed to load PDF'}</p>
                            <button onclick="window.close()">Close</button>
                        </div>
                    </body>
                    </html>
                `);
                printWindow.document.close();
            }
            
            toast.error(error.response?.data?.message || 'Failed to prepare document for printing');
        }
    };

    // Safe calculations with proper null checks
    const totalDiscount = bill?.billItems?.reduce((acc: number, item: any) => acc + (item.discountAmount || 0), 0) || 0;
    const totalAmountWithoutDiscount = bill?.billItems?.reduce((acc: number, item: any) => acc + ((item.unitPrice || 0) * (item.quantity || 0)), 0) || 0;

    const amountPaid = useMemo(() => getSplitsTotal(splits), [splits]);

    // Early return after all hooks are declared
    if (!billId || !isOpen) {
        return null;
    }

    // Loading state
    if (isLoading) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            View Bill
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin mr-2" />
                        <span className="text-sm text-gray-600">Loading bill details...</span>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Error state
    if (error || !bill) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                            <Receipt className="h-5 w-5" />
                            Bill Not Found
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center justify-center py-12">
                        <Receipt className="h-16 w-16 text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-700 mb-2">Bill Not Found</h3>
                        <p className="text-gray-500 text-center mb-6">
                            {error instanceof Error ? error.message : 'The requested bill could not be found.'}
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Bill Details
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6">
                        {/* Bill Header */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">{bill.billNumber}</h3>
                                    <p className="text-sm text-gray-600">Generated on {formatDateTime(bill.billDate)} IST</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(bill.status).replace(/_/g, ' ')}`}>
                                        {bill.status.replace(/_/g, ' ')}
                                    </span>
                                    <Button
                                        onClick={handleDownloadBill}
                                        variant="outline"
                                        size="sm"
                                        disabled={downloadingBill}
                                        className="flex items-center gap-2"
                                    >
                                        {downloadingBill ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Download className="h-4 w-4" />
                                        )}
                                        {downloadingBill ? 'Downloading...' : 'Download PDF'}
                                    </Button>

                                    <Button
                                        onClick={handlePrint}
                                        variant="outline"
                                        className="flex items-center text-green-600 hover:text-green-700 print:hidden"
                                        title="Print using the formatted template"
                                    >
                                        <FileText className="h-4 w-4 mr-2" />
                                        Print Bill
                                    </Button>
                                </div>
                            </div>

                            {/* Bill Summary */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-gray-900">₹{bill.totalAmount.toFixed(2)}</div>
                                    <div className="text-sm text-gray-500">Total Amount</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-xl font-semibold text-green-600">₹{bill.paidAmount.toFixed(2)}</div>
                                    <div className="text-sm text-gray-500">Paid Amount</div>
                                </div>
                                <div className="text-center">
                                    <div className={`text-xl font-semibold ${bill.dueAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        ₹{bill.dueAmount.toFixed(2)}
                                    </div>
                                    <div className="text-sm text-gray-500">Due Amount</div>
                                </div>
                            </div>
                        </div>

                        {/* Patient and Hospital Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Patient Information */}
                            {bill.patient && (
                                <div className="bg-white border rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <User className="h-5 w-5 text-blue-600" />
                                        <h3 className="font-semibold text-gray-900">Patient Information</h3>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <div><span className="font-medium">Name:</span> {bill.patient.name}</div>
                                        <div><span className="font-medium">ID:</span> {bill.patient?.uhid}</div>
                                        {(bill.patient as any)?.dob && (
                                            <div>
                                                <span className="font-medium">Age / Sex:</span>{" "}
                                                {(() => {
                                                    const dob = new Date((bill.patient as any).dob);
                                                    const today = new Date();
                                                    let age = today.getFullYear() - dob.getFullYear();
                                                    const m = today.getMonth() - dob.getMonth();
                                                    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                                                    return Number.isFinite(age) && age >= 0 ? age : '—';
                                                })()}
                                                {" / "}
                                                {(bill.patient as any)?.gender ?? '—'}
                                            </div>
                                        )}
                                        {bill.patient.phone && (
                                            <div><span className="font-medium">Phone:</span> {bill.patient.phone}</div>
                                        )}
                                        {bill.patient.email && (
                                            <div><span className="font-medium">Email:</span> {bill.patient.email}</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Hospital Information */}
                            {bill.hospital && (
                                <div className="bg-white border rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Building2 className="h-5 w-5 text-green-600" />
                                        <h3 className="font-semibold text-gray-900">Hospital Information</h3>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <div><span className="font-medium">Name:</span> {bill.hospital.name}</div>
                                        {bill.hospital.address && (
                                            <div><span className="font-medium">Address:</span> {bill.hospital.address}</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Appointment Info (if exists) */}
                        {bill.appointment && (
                            <div className="bg-white border rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Calendar className="h-5 w-5 text-purple-600" />
                                    <h3 className="font-semibold text-gray-900">Appointment Details</h3>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4 text-sm">
                                    <div><span className="font-medium">Date & Time:</span> {new Date(bill.appointment.scheduledAt).toLocaleString('en-GB', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        timeZone: 'Asia/Kolkata'
                                    })}</div>
                                    <div><span className="font-medium">Visit Type:</span> {bill.appointment.visitType}</div>
                                    {bill.appointment.doctor && (
                                        <>
                                            <div className="flex items-center gap-2">
                                                <Stethoscope className="h-4 w-4 text-gray-500" />
                                                <span><span className="font-medium">Doctor:</span> {ensureDoctorPrefix(bill.appointment.doctor.name)}</span>
                                            </div>
                                            <div><span className="font-medium">Specialization:</span> {bill.appointment.doctor.specialisation}</div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Bill Items */}
                        {bill.billItems && bill.billItems.length > 0 && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <FlaskConical className="h-5 w-5 text-orange-600" />
                                    <h4 className="font-semibold text-gray-700">Bill Items</h4>
                                </div>
                                <div className="bg-white border rounded-lg overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Description</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap">Batch</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap">Expiry</th>
                                                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Qty</th>
                                                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Unit Price</th>
                                                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Discount</th>
                                                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {bill.billItems.map((item: any, index: number) => (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        <div>
                                                            <div className="font-medium">{item.description}</div>
                                                            {item.notes && (
                                                                <div className="text-xs text-gray-500 mt-1">{item.notes}</div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                                                        {item?.pharmacyIssue?.batch?.batchNo ?? '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                                        {item?.pharmacyIssue?.batch?.expiryDate
                                                            ? String(item.pharmacyIssue.batch.expiryDate).slice(0, 10)
                                                            : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                        {editingItemId === item.id ? (
                                                            <div className="flex items-center justify-end gap-2">
                                                                <Input
                                                                    type="number"
                                                                    step="0.01"
                                                                    inputMode="decimal"
                                                                    value={unitPriceDraft}
                                                                    onChange={(e) => setUnitPriceDraft(e.target.value)}
                                                                    className="h-9 w-28 text-right"
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="h-8 w-8"
                                                                    disabled={updateBillItemMutation.isPending}
                                                                    onClick={() => {
                                                                        const v = parseFloat(unitPriceDraft);
                                                                        if (!bill?.id || !item?.id) return;
                                                                        if (isNaN(v) || v < 0) {
                                                                            toast.error('Enter a valid amount');
                                                                            return;
                                                                        }
                                                                        updateBillItemMutation.mutate({ billId: bill.id, itemId: item.id, unitPrice: v });
                                                                    }}
                                                                    title="Save"
                                                                >
                                                                    {updateBillItemMutation.isPending ? (
                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                    ) : (
                                                                        <Check className="h-4 w-4" />
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="h-8 w-8"
                                                                    disabled={updateBillItemMutation.isPending}
                                                                    onClick={() => {
                                                                        setEditingItemId(null);
                                                                        setUnitPriceDraft("");
                                                                    }}
                                                                    title="Cancel"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center justify-end gap-2">
                                                                <span>₹{item.unitPrice.toFixed(2)}</span>
                                                                {canEditLineUnitPrice({
                                                                    itemType: item.itemType,
                                                                    isOutpatientBillAdminEditor,
                                                                    userRole: user?.role,
                                                                    billPaidAmount: bill.paidAmount ?? 0,
                                                                    paymentCount: bill.payments?.length ?? 0,
                                                                }) && (
                                                                        <Button
                                                                            type="button"
                                                                            size="icon"
                                                                            variant="ghost"
                                                                            className="h-8 w-8 text-slate-600 hover:text-slate-900"
                                                                            onClick={() => {
                                                                                setEditingItemId(item.id);
                                                                                setUnitPriceDraft(String(item.unitPrice ?? ''));
                                                                            }}
                                                                            title="Edit line amount"
                                                                        >
                                                                            <Pencil className="h-4 w-4" />
                                                                        </Button>
                                                                    )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-green-600 text-right">
                                                        {item.discountAmount > 0 ? `-₹${item.discountAmount.toFixed(2)}` : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">₹{item.totalPrice.toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-gray-50">
                                            <tr>
                                                <td colSpan={6} className="px-4 py-3 text-right font-semibold text-gray-900">Total:</td>
                                                <td className="px-4 py-3 text-right font-bold text-lg text-gray-900">₹{bill.totalAmount.toFixed(2)}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Payment Summary with Discount Info */}
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                                <IndianRupee className="h-4 w-4" />
                                Payment Summary
                            </h4>

                            {/* Display original price vs final price if discount applied */}
                            <div className="flex items-center gap-3 text-lg mb-2">
                                <span className="font-bold text-xl text-black">₹{bill.totalAmount.toFixed(2)}</span>
                                {totalDiscount > 0 && (
                                    <>
                                        <span className="line-through text-gray-400 text-base">₹{totalAmountWithoutDiscount.toFixed(2)}</span>
                                        <span className="text-green-600 font-semibold text-base">
                                            {Math.round((totalDiscount / totalAmountWithoutDiscount) * 100)}% off
                                        </span>
                                    </>
                                )}
                            </div>

                            <div className="mt-4 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Amount Paid:</span>
                                    <span className="font-medium text-green-600">₹{bill.paidAmount.toFixed(2)}</span>
                                </div>
                                <div className="border-t pt-2 flex justify-between font-semibold text-lg">
                                    <span>Amount Due:</span>
                                    <span className={bill.dueAmount > 0 ? 'text-red-600' : 'text-green-600'}>
                                        ₹{bill.dueAmount.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Payment History */}
                        {bill.payments && bill.payments.length > 0 && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <IndianRupee className="h-5 w-5 text-green-600" />
                                    <h4 className="font-semibold text-gray-700">Payment History</h4>
                                </div>
                                <div className="space-y-3">
                                    {bill.payments.map((payment: any, index: number) => (
                                        <div key={payment.id || index} className="bg-white border rounded-lg p-4">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <div className="font-medium">₹{payment.amount >= 0 ? payment.amount.toFixed(2) : `-${Math.abs(payment.amount).toFixed(2)} (Refund)`}</div>
                                                    <div className="text-sm text-gray-500 flex flex-wrap items-center gap-2">
                                                        {isOutpatientBillAdminEditor &&
                                                        editingPaymentId === payment.id &&
                                                        payment.status === 'COMPLETED' &&
                                                        payment.amount > 0 ? (
                                                            <div className="flex items-center gap-2">
                                                                <Select
                                                                    value={paymentMethodDraft}
                                                                    onValueChange={(v) =>
                                                                        setPaymentMethodDraft(v as PaymentMethod)
                                                                    }
                                                                    disabled={updatePaymentMethodMutation.isPending}
                                                                >
                                                                    <SelectTrigger className="h-8 w-[140px]">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {Object.values(PaymentMethod).map((m) => (
                                                                            <SelectItem key={m} value={m}>
                                                                                {String(m).replace(/_/g, ' ')}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <Button
                                                                    type="button"
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="h-8 w-8"
                                                                    disabled={updatePaymentMethodMutation.isPending}
                                                                    onClick={() =>
                                                                        updatePaymentMethodMutation.mutate({
                                                                            paymentId: payment.id,
                                                                            paymentMethod: paymentMethodDraft,
                                                                        })
                                                                    }
                                                                    title="Save payment mode"
                                                                >
                                                                    {updatePaymentMethodMutation.isPending ? (
                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                    ) : (
                                                                        <Check className="h-4 w-4" />
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="h-8 w-8"
                                                                    disabled={updatePaymentMethodMutation.isPending}
                                                                    onClick={() => setEditingPaymentId(null)}
                                                                    title="Cancel"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <span>{payment.paymentMethod?.replace(/_/g, ' ')}</span>
                                                                {isOutpatientBillAdminEditor &&
                                                                    payment.status === 'COMPLETED' &&
                                                                    payment.amount > 0 && (
                                                                        <Button
                                                                            type="button"
                                                                            size="icon"
                                                                            variant="ghost"
                                                                            className="h-7 w-7 text-slate-600 hover:text-slate-900"
                                                                            onClick={() => {
                                                                                setEditingPaymentId(payment.id);
                                                                                setPaymentMethodDraft(
                                                                                    payment.paymentMethod as PaymentMethod
                                                                                );
                                                                            }}
                                                                            title="Edit payment mode"
                                                                        >
                                                                            <Pencil className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                    )}
                                                            </>
                                                        )}
                                                        <span>• {formatDate(payment.paymentDate)}</span>
                                                    </div>
                                                    {payment.notes && (
                                                        <div className="text-xs text-gray-400 mt-1">{payment.notes}</div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.status)}`}>
                                                        {payment.status}
                                                    </span>
                                                    {payment.status === 'COMPLETED' && payment.amount > 0 && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-amber-600 hover:text-amber-700 border-amber-200"
                                                            onClick={() => {
                                                                if (window.confirm(`Refund ₹${payment.amount.toFixed(2)}?`)) {
                                                                    refundPaymentMutation.mutate({ paymentId: payment.id });
                                                                }
                                                            }}
                                                            disabled={refundPaymentMutation.isPending}
                                                        >
                                                            Refund
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        {bill.notes && (
                            <div className="space-y-2">
                                <h4 className="font-semibold text-gray-700">Notes</h4>
                                <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-700">
                                    {bill.notes}
                                </div>
                            </div>
                        )}

                        {bill.patient && bill.dueAmount > 0 && (
                            <LoyaltyBillingPanel
                                bill={bill}
                                onApplied={() => {
                                    queryClient.invalidateQueries({ queryKey: ['bill', billId] });
                                    if (onSuccess) onSuccess();
                                }}
                            />
                        )}

                        {/* Actions */}
                        <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
                            {canFinalizeDraftBill && bill.status === BillStatus.DRAFT && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="text-red-700 border-red-200 hover:bg-red-50"
                                    disabled={cancelDraftBillMutation.isPending}
                                    onClick={() => {
                                        if (window.confirm('Cancel this draft bill? Stock will be restored for pharmacy lines.')) {
                                            cancelDraftBillMutation.mutate();
                                        }
                                    }}
                                >
                                    Cancel bill
                                </Button>
                            )}
                            {canFinalizeDraftBill &&
                                bill.status === BillStatus.DRAFT &&
                                bill.dueAmount <= 0 &&
                                (bill.payments?.length ?? 0) === 0 && (
                                <Button
                                    type="button"
                                    className="bg-green-600 hover:bg-green-700"
                                    disabled={markDraftPaidMutation.isPending}
                                    onClick={() => markDraftPaidMutation.mutate()}
                                >
                                    Mark as paid
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                            >
                                Close
                            </Button>

                            {bill.status !== BillStatus.CANCELLED && bill.dueAmount > 0 && (
                                <>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => handlePaymentClick('partial')}
                                        className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-200"
                                    >
                                        Pay Partial Amount
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={() => handlePaymentClick('full')}
                                        className="bg-green-600 hover:bg-green-700"
                                    >
                                        Pay Full Amount (₹{bill.dueAmount.toFixed(2)})
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Payment Dialog */}
            {showPaymentDialog && (
                <Dialog open={showPaymentDialog} onOpenChange={() => setShowPaymentDialog(false)}>
                    <DialogContent className="sm:max-w-[500px]">
                        <DialogHeader>
                            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                                <IndianRupee className="h-5 w-5" />
                                {paymentType === 'full' ? 'Pay Full Amount' : 'Pay Partial Amount'}
                            </DialogTitle>
                        </DialogHeader>

                        <form onSubmit={handlePaymentSubmit} className="space-y-4">
                            {/* Payment Summary */}
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="text-sm space-y-1">
                                    <div className="flex justify-between">
                                        <span>Amount Due:</span>
                                        <span className="font-medium">₹{bill?.dueAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Amount to Pay:</span>
                                        <span className="font-medium text-green-600">₹{amountPaid.toFixed(2)}</span>
                                    </div>
                                    {paymentType === 'partial' && (
                                        <div className="flex justify-between">
                                            <span>Remaining:</span>
                                            <span className="font-medium text-red-600">₹{(bill?.dueAmount - amountPaid).toFixed(2)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <PaymentSplitsEditor
                                splits={splits}
                                onChange={setSplits}
                                maxTotal={bill?.dueAmount ?? 0}
                                disabled={processingPayment}
                                label={paymentType === "full" ? "Payment modes (full amount)" : "Payment modes (partial amount)"}
                            />

                            {/* Notes */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Notes (Optional)</Label>
                                <textarea
                                    value={paymentNotes}
                                    onChange={(e) => setPaymentNotes(e.target.value)}
                                    placeholder="Add any payment notes..."
                                    className="w-full min-h-[60px] p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={processingPayment}
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-2 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowPaymentDialog(false)}
                                    disabled={processingPayment}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="bg-blue-600 hover:bg-blue-700"
                                    disabled={processingPayment || !!getSplitsError({ splits, maxTotal: bill?.dueAmount ?? 0 })}
                                >
                                    {processingPayment ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        `Pay ₹${amountPaid.toFixed(2)}`
                                    )}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
} 